import { useId, useState } from "react";
import { TextInput, Typography } from "@/components/atoms";
import { NotificationEmailPair } from "@/redux/apis/clientProfile/types";

export interface NotificationCategoryRowProps {
  label: string;
  description: string;
  mode: "display" | "edit";
  value: NotificationEmailPair;
  /** In display mode, the primary falls back to this (the default Notification Email) when the category has no primary of its own. */
  defaultPrimary?: string | null;
  onChange?: (value: NotificationEmailPair) => void;
  error?: { primary?: string; secondary?: string };
}

const DEFAULT_LABEL = "Notification Email";

export const NotificationCategoryRow = ({
  label,
  description,
  mode,
  value,
  defaultPrimary,
  onChange,
  error,
}: NotificationCategoryRowProps) => {
  const primaryErrorId = useId();
  const secondaryErrorId = useId();
  // Secondary is shown when it already has a value (incl. empty string set by "Add"),
  // or once the user clicks "Add another email". `null` means "not set / hidden".
  const [showSecondary, setShowSecondary] = useState(value.secondary !== null && value.secondary !== undefined);

  const primaryPlaceholder = label === DEFAULT_LABEL ? "Enter email for default notifications" : `Enter email for ${label}`;

  const handlePrimaryChange = (primary: string) => onChange?.({ ...value, primary });
  const handleSecondaryChange = (secondary: string) => onChange?.({ ...value, secondary });

  const handleAddSecondary = () => {
    setShowSecondary(true);
    onChange?.({ ...value, secondary: "" });
  };

  const handleRemoveSecondary = () => {
    setShowSecondary(false);
    onChange?.({ ...value, secondary: null });
  };

  const renderLabel = () => (
    <div className="flex flex-col gap-0.5">
      <Typography variant="paragraph" className="font-semibold text-neutral-black">
        {label}
      </Typography>
      <Typography variant="small-regular" className="text-neutral-dark-gray">
        {description}
      </Typography>
    </div>
  );

  if (mode === "display") {
    // Primary and secondary resolve independently (mirrors the backend): the primary inherits the
    // default Notification Email when the category has none of its own, while the secondary is always
    // the category's own and is never inherited from the default.
    const resolvedPrimary = (value.primary || defaultPrimary) || null;
    const resolvedSecondary = value.secondary || null;

    const renderEmailLine = (lineLabel: string, email: string | null) => (
      <div className="flex flex-col gap-1 @[560px]:flex-row @[560px]:items-start @[560px]:gap-2.5">
        <Typography variant="paragraph" className="min-w-[76px] shrink-0 text-neutral-silver-gray">
          {lineLabel}
        </Typography>
        <Typography variant="action" className="min-w-0 break-all text-neutral-black">
          {email ?? "-"}
        </Typography>
      </div>
    );

    return (
      <div className="grid grid-cols-1 items-start gap-2 py-4 @[560px]:grid-cols-[220px_minmax(0,1fr)] @[560px]:gap-x-12 @[560px]:gap-y-0">
        {renderLabel()}
        <div className="flex min-w-0 flex-col gap-1">
          {renderEmailLine("Primary:", resolvedPrimary)}
          {renderEmailLine("Secondary:", resolvedSecondary)}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 items-start gap-2 py-4 @[560px]:grid-cols-[220px_minmax(0,1fr)] @[560px]:gap-x-12 @[560px]:gap-y-0">
      {renderLabel()}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-1">
          <TextInput
            type="email"
            size="md"
            data-testid="primary-input"
            aria-label={`${label} primary email`}
            aria-invalid={!!error?.primary}
            aria-describedby={error?.primary ? primaryErrorId : undefined}
            placeholder={primaryPlaceholder}
            value={value.primary ?? ""}
            hasError={!!error?.primary}
            onChange={(e) => handlePrimaryChange(e.target.value)}
          />
          {error?.primary && (
            <Typography id={primaryErrorId} role="alert" variant="small-regular" className="text-danger">
              {error.primary}
            </Typography>
          )}
        </div>

        {showSecondary ? (
          <div className="flex flex-col gap-1">
            <TextInput
              type="email"
              size="md"
              data-testid="secondary-input"
              aria-label={`${label} secondary email`}
              aria-invalid={!!error?.secondary}
              aria-describedby={error?.secondary ? secondaryErrorId : undefined}
              placeholder="Enter secondary email"
              value={value.secondary ?? ""}
              hasError={!!error?.secondary}
              onChange={(e) => handleSecondaryChange(e.target.value)}
            />
            {error?.secondary && (
              <Typography id={secondaryErrorId} role="alert" variant="small-regular" className="text-danger">
                {error.secondary}
              </Typography>
            )}
            <button type="button" onClick={handleRemoveSecondary} className="self-start text-sm font-semibold text-danger hover:underline">
              - Remove secondary email
            </button>
          </div>
        ) : (
          <button type="button" onClick={handleAddSecondary} className="self-start text-sm font-semibold text-info hover:underline">
            + Add another email
          </button>
        )}
      </div>
    </div>
  );
};
